<template>
  <div class="grid grid-cols-1 gap-4 md:grid-cols-2 md:items-end xl:grid-cols-3 2xl:grid-cols-5">
    <Input v-model="searchModel" :placeholder="t('search')" variant="ghost" class="flex-1" />
    <Select
      v-model="periodicFilterModel"
      :label="t('bookingType')"
      :options="periodicOptions"
      empty-value-to="null"
      class="min-w-[180px]"
    />
    <Select
      v-model="displayedInvoicedFilter"
      :label="t('invoicing')"
      :options="invoicedOptions"
      :disabled="invoiceFiltersDisabled"
      empty-value-to="null"
      class="min-w-[180px]"
    />
    <Select
      v-model="depositFilterModel"
      :label="t('deposit')"
      :options="depositOptions"
      empty-value-to="null"
      class="min-w-[180px]"
    />
    <InputLabel :label="t('invoiceTypes')">
      <MultiSelect
        v-model="displayedInvoiceTypes"
        :options="invoiceTypeOptions"
        :placeholder="t('select')"
        :disabled="invoiceFiltersDisabled || displayedInvoicedFilter === 'uninvoiced'"
      />
    </InputLabel>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Input from '@/components/Input.vue';
import Select from '@/components/Select.vue';
import InputLabel from '@/components/InputLabel.vue';
import MultiSelect from '@/components/MultiSelect.vue';
import { InvoiceType } from '@carvia/ros-client-types';

const props = defineProps<{
  invoiceFiltersDisabled?: boolean;
}>();

const searchModel = defineModel<string>('search', { default: '' });
const periodicFilterModel = defineModel<string | null>('periodicFilter', { default: null });
const invoicedFilterModel = defineModel<string | null>('invoicedFilter', { default: null });
const depositFilterModel = defineModel<string | null>('depositFilter', { default: null });
const selectedInvoiceTypesModel = defineModel<string[]>('selectedInvoiceTypes', { default: [] });

const displayedInvoicedFilter = computed({
  get: () => (props.invoiceFiltersDisabled ? 'uninvoiced' : invoicedFilterModel.value),
  set: (value) => {
    invoicedFilterModel.value = value;
  },
});

const displayedInvoiceTypes = computed({
  get: () => (props.invoiceFiltersDisabled ? [] : selectedInvoiceTypesModel.value),
  set: (value) => {
    selectedInvoiceTypesModel.value = value;
  },
});

const { t } = useI18n();
const { t: tGlobal } = useI18n({ useScope: 'global' });

const invoiceTypeOptions = computed(() =>
  Object.values(InvoiceType).map((type) => ({
    label: tGlobal(`invoiceTypes.${type}`, type),
    value: type,
  })),
);

const periodicOptions = computed(() => [
  { label: t('all'), value: null },
  { label: t('longTermRental'), value: 'true' },
  { label: t('shortTermRental'), value: 'false' },
]);

const invoicedOptions = computed(() => [
  { label: t('all'), value: null },
  { label: t('invoiced'), value: 'invoiced' },
  { label: t('uninvoiced'), value: 'uninvoiced' },
]);

const depositOptions = computed(() => [
  { label: t('all'), value: null },
  { label: t('only'), value: 'true' },
  { label: t('without'), value: 'false' },
]);
</script>

<i18n lang="json">
{
  "en": {
    "search": "Search",
    "invoiceTypes": "Invoice types",
    "invoicing": "Invoicing",
    "invoiced": "Invoiced",
    "uninvoiced": "Uninvoiced",
    "bookingType": "Booking type",
    "longTermRental": "Long-term rental",
    "shortTermRental": "Short-term rental",
    "deposit": "Deposit",
    "only": "Only",
    "without": "Without",
    "all": "All",
    "select": "Select"
  },
  "de": {
    "search": "Suche",
    "invoiceTypes": "Rechnungstypen",
    "invoicing": "Fakturierung",
    "invoiced": "Fakturiert",
    "uninvoiced": "Nicht fakturiert",
    "bookingType": "Buchungstyp",
    "longTermRental": "Langzeitmiete",
    "shortTermRental": "Kurzzeitmiete",
    "deposit": "Kaution",
    "only": "Nur",
    "without": "Ohne",
    "all": "Alle",
    "select": "Auswählen"
  },
  "es": {
    "search": "Buscar",
    "invoiceTypes": "Tipos de factura",
    "invoicing": "Facturación",
    "invoiced": "Facturado",
    "uninvoiced": "Sin facturar",
    "bookingType": "Tipo de reserva",
    "longTermRental": "Alquiler a largo plazo",
    "shortTermRental": "Alquiler a corto plazo",
    "deposit": "Depósito",
    "only": "Solo",
    "without": "Sin",
    "all": "Todos",
    "select": "Seleccionar"
  }
}
</i18n>
